import {
  Col,
  AlignItems,
  Box,
  Display,
  FlexDirection,
  Heading1,
  JustifyContent,
  Grid,
} from '@gousto-internal/citrus-react'
import { datadogRum } from '@datadog/browser-rum'

import { useDashboardExperience } from './hooks/useDashboardExperience'
import { Notification } from './components/Notification'
import React, { useEffect, useState } from 'react'
import { OrderTabs, OrderTile } from './components/OrderTile/index'
import { useOrderTracking } from './hooks/useOrderTracking'

const getDefaultActiveTabIndex = (
  orderTabs: ReturnType<typeof useDashboardExperience>['order_tabs'],
) => {
  const defaultActiveIndex = orderTabs.findIndex((tab) => tab.default_active)

  return defaultActiveIndex === -1 ? 0 : defaultActiveIndex
}

const getActiveTabIndex = (
  orderTabs: ReturnType<typeof useDashboardExperience>['order_tabs'],
  activeOrderId: string | undefined,
) => {
  const activeTabIndexFromOrderId = orderTabs.findIndex((tab) => tab.order_id === activeOrderId)

  return activeTabIndexFromOrderId === -1
    ? getDefaultActiveTabIndex(orderTabs)
    : activeTabIndexFromOrderId
}

export function MyHomePage() {
  const { page_title, order_tabs, error, orders = [] } = useDashboardExperience()
  const { trackHomeOrdersTabClick } = useOrderTracking()
  const [activeOrderId, setActiveOrderId] = useState<string>()
  const hasOrderMismatch = orders.length !== order_tabs.length

  const activeTabIndex = getActiveTabIndex(order_tabs, activeOrderId)

  const activeOrder = orders.find((order) => order.id === activeOrderId)

  useEffect(() => {
    if (!order_tabs.length) {
      return
    }

    if (activeOrderId && order_tabs.some((tab) => tab.order_id === activeOrderId)) {
      return
    }

    const defaultActiveTab = order_tabs[getDefaultActiveTabIndex(order_tabs)]
    setActiveOrderId(defaultActiveTab.order_id)
  }, [order_tabs, activeOrderId])

  const handleOrderTabChange = (index: number) => {
    const selectedTab = order_tabs[index]

    setActiveOrderId(selectedTab.order_id)

    const order = orders.find((currentOrder) => currentOrder.id === selectedTab.order_id)

    if (order) {
      trackHomeOrdersTabClick({
        order,
        tabPosition: index + 1,
      })
    }
  }

  useEffect(() => {
    if (hasOrderMismatch) {
      datadogRum.addAction('dashboard_experience_order_mismatch', {
        page: 'MyHomePage',
        selectedIndex: activeTabIndex,
        orderTabsLength: order_tabs.length,
        ordersLength: orders.length,
      })
    }
  }, [hasOrderMismatch, activeTabIndex, order_tabs.length, orders.length])

  return (
    <main data-testid="my-home-page-main">
      <Box
        maxWidth="1200px"
        style={{ margin: '0 auto' }}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.FlexStart}
        gap={[4, 6]}
        paddingV={[4, 6]}
        paddingH={[3, 0]}
      >
        <Box
          width="100%"
          minHeight={['52px', '40px']}
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.FlexStart}
          paddingV={[3, 1]}
          paddingH={[3, 0]}
          data-testid="my-home-page-title-box"
        >
          {error ? (
            <Notification
              state="error"
              message="Oops something went wrong. Please try again later."
            />
          ) : (
            <Heading1>{page_title}</Heading1>
          )}
        </Box>

        <Grid gap={[4, 3]} width={'100%'}>
          <Col size={[12, null, null, 2]} display={[Display.None, null, null, Display.Block]}>
            <Box>
              <section aria-label="order-tabs">
                <Box>
                  <OrderTabs
                    orderTabs={order_tabs}
                    activeIndex={activeTabIndex}
                    onChange={handleOrderTabChange}
                  />
                </Box>
              </section>
            </Box>
          </Col>
          <Col size={[12, null, null, 10]}>
            <Box
              width="100%"
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              gap={[4, 3]}
            >
              <section aria-label="Right column">
                {!hasOrderMismatch && activeOrder ? (
                  <Box width="100%" display={[Display.Flex, null, null, Display.None]}>
                    <section
                      aria-label="order-tabs-mobile"
                      style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
                    >
                      <OrderTabs
                        orderTabs={order_tabs}
                        activeIndex={activeTabIndex}
                        onChange={handleOrderTabChange}
                        variant="mobile"
                      />
                    </section>
                  </Box>
                ) : null}
                {hasOrderMismatch ? (
                  <Notification
                    state="error"
                    message="Oops something went wrong. Please try again later."
                  />
                ) : activeOrder ? (
                  <OrderTile order={activeOrder} />
                ) : null}
              </section>
            </Box>
          </Col>
        </Grid>
      </Box>
    </main>
  )
}
